import Script from 'next/script';
import { G } from '@mobily/ts-belt';
import {
  isRegistrationError,
  isRegistrationResponseExisting,
  registerUser,
} from '../../utils/requests/clientAuth/registration';
import type { SocialButtonsProps } from './SocialButtons';
import { loginByFacebookToken } from '../../utils/requests/clientAuth/login';
import { isNonEmptyString } from '../../utils/helpers';
import {
  isAndroidApp,
  loginWithFacebook,
} from '../../utils/registration/android';
import { SocialButton } from './SocialButton';

const INIT_DATA = {
  appId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID,
  autoLogAppEvents: true,
  status: true,
  xfbml: true,
  version: 'v5.0',
};

/** Helper class to propagate any error metadata as a 'cause' field */
class FacebookError extends Error {
  source: FacebookButtonErrorSource;

  cause?: Record<string, unknown>;

  constructor(
    message: string,
    source: FacebookButtonErrorSource,
    cause?: Record<string, unknown>,
  ) {
    super(message);
    this.source = source;
    this.cause = cause;
  }
}

/**
 * > The /me node is a special endpoint that translates to the object ID of the
 * > person or Page whose access token is currently being used to make the API calls
 * >
 * > — https://developers.facebook.com/docs/graph-api/overview/#me
 *
 * in our usage we are _always_ requesting a person, so the [User](https://developers.facebook.com/docs/graph-api/reference/user/)
 * is the relevant portion of the documentation. There are varying permissions a
 * user can grant, so we are including the ones we use from the "Default Public
 * Profile Fields" and acknowledging it is possible we will not recieve an email address.
 */
export type FacebookUserProfileData = {
  first_name: string;
  last_name: string;
  email: string;
};

type FacebookButtonErrorSource = 'facebook' | 'client_auth' | 'missing_email';

export type OnFacebookErrorParams = {
  source: FacebookButtonErrorSource;
  message?: string;
  cause?: unknown;
};

type FacebookButtonProps = Exclude<SocialButtonsProps, 'onError'> & {
  onError: (params: OnFacebookErrorParams) => void;
};

type FBStatusResponse = Parameters<Parameters<typeof FB.getLoginStatus>[0]>[0];

/**
 * The first step when your webpage loads is determining if a person is already logged into your webpage with Facebook Login.
 * Source: https://developers.facebook.com/docs/facebook-login/web#checklogin
 */
const getCurrentFacebookLogin = () => {
  return new Promise<FBStatusResponse>(resolve => {
    FB.getLoginStatus(response => resolve(response));
  });
};

/**
 * This function opens a pop up and defines a callback for the successful login with the popup
 * Source: https://developers.facebook.com/docs/reference/javascript/FB.login/v15.0
 */
const promptNewFacebookLogin = () => {
  return new Promise<FBStatusResponse>(resolve => {
    FB.login(response => resolve(response), { scope: 'public_profile,email' });
  });
};

const facebookProfileFields = ['first_name', 'last_name', 'email'] as const;

type FacebookProfileFields = (typeof facebookProfileFields)[number];

/**
 * This request allows us to retrieve user profile data from facebook
 */
const getFacebookProfileData = () => {
  return new Promise<FacebookUserProfileData>((resolve, reject) => {
    FB.api<FacebookProfileFields>(
      '/me',
      { fields: [...facebookProfileFields] },
      profileData => {
        const { email, first_name, last_name } = profileData;

        if (
          G.isString(email) &&
          G.isString(first_name) && // TODO: figure out if we need to require first_name and last_name
          G.isString(last_name)
        ) {
          return resolve({
            email,
            first_name,
            last_name,
          });
        }

        return reject(
          new FacebookError('Failed to get Facebook email', 'missing_email', {
            profileData,
          }),
        );
      },
    );
  });
};

type FBStatusResponseWithAccessToken = FBStatusResponse & {
  status: 'connected';
  authResponse: FBStatusResponse['authResponse'] & {
    accessToken: string;
  };
};

/**
 * FB only provides the accessToken when the user is connected. These are the possible statuses:
 * - the user is logged into Facebook and has authenticated your application (connected)
 * - the user is logged into Facebook but has not authenticated your application (not_authorized)
 * - the user is either not logged into Facebook or explicitly logged out of your application so it doesn't attempt to connect to Facebook and thus, we don't know if they've authenticated your application or not (unknown)
 *
 * Docs: https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus#login-status
 */
const hasFacebookAccessToken = (
  response: FBStatusResponse,
): response is FBStatusResponseWithAccessToken =>
  G.isObject(response) &&
  response.status === 'connected' &&
  isNonEmptyString(response.authResponse?.accessToken);

/**
 * Follows the Facebook Login flow to get the access token
 * Docs: https://developers.facebook.com/docs/facebook-login/web
 *
 * @returns the Facebook access token or throws an error with the payload we received
 */
const getFacebookAccessToken = async () => {
  if (typeof FB === 'undefined') {
    throw new FacebookError('Facebook SDK is not loaded', 'facebook');
  }

  // 1. Check if the user is already logged in
  const currentFacebookLogin = await getCurrentFacebookLogin();

  if (hasFacebookAccessToken(currentFacebookLogin)) {
    return currentFacebookLogin.authResponse.accessToken;
  }

  // 2. Prompt the user to log in, opens the FB pop up
  const newFacebookLogin = await promptNewFacebookLogin();

  if (hasFacebookAccessToken(newFacebookLogin)) {
    return newFacebookLogin.authResponse.accessToken;
  }

  throw new FacebookError(
    `Failed to get Facebook accessToken – status: ${newFacebookLogin.status}`,
    'facebook',
    {
      payloads: { currentFacebookLogin, newFacebookLogin },
    },
  );
};

/**
 * This component enables the ability to open a Facebook SSO pop up and provide
 * callbacks to interact with the result of the pop up.
 */
export const FacebookButton = (props: FacebookButtonProps) => {
  const handleClick = async () => {
    try {
      if (isAndroidApp()) {
        return loginWithFacebook('public_profile,email');
      }

      const accessToken = await getFacebookAccessToken();

      if (props.mode !== 'signup') {
        const clientAuthResponse = await loginByFacebookToken(
          accessToken,
          props.isWafEnabled,
        );

        return props.onLogin(clientAuthResponse.data);
      }
      const { first_name, last_name, email } = await getFacebookProfileData();

      const clientAuthResponse = await registerUser(
        {
          user: {
            first_name,
            last_name,
            email,
            business_line: props.businessLine,
            shipping_postcode: props.zipCode,
            signup_type: props.registrationSignupType,
          },
          options: {
            fb_oauth_token: accessToken,
          },
        },
        props.isWafEnabled,
      );

      if (isRegistrationResponseExisting(clientAuthResponse.data)) {
        return props.onLogin(clientAuthResponse.data);
      }

      return props.onRegistration(clientAuthResponse.data);
    } catch (error) {
      if (error instanceof FacebookError) {
        return props.onError({
          source: error.source,
          message: error.message,
          cause: error.cause,
        });
      }

      return props.onError({
        source: 'client_auth',
        message: 'Client Auth API request failed',
        cause: isRegistrationError(error) ? error.response.data : error,
      });
    }
  };

  return (
    <>
      <SocialButton
        variant="facebook"
        onClick={handleClick}
        compact={props.inline}
        mode={props.mode}
      />

      <Script id="fb-async-init">
        {`
          window.fbAsyncInit = function() {
            FB.init(${JSON.stringify(INIT_DATA)});
          };
        `}
      </Script>

      <Script async defer src="https://connect.facebook.net/en_US/sdk.js" />
    </>
  );
};
